// src/mobile_pages/const/UserTemplate_view.jsx
import { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Save,
  Trash2,
  FileText
} from "lucide-react";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import {
  getUserTemplate,
  updateUserTemplate,
  deleteUserTemplate,
  saveUserTemplate,
} from "../../api/const";

export default function UserTemplateViewPage() {
  const { id, templateId } = useParams();
  const navigate = useNavigate();

  const isNew = !templateId; // templateId 없으면 신규 모드

  const editorRef = useRef(null);
  const [editorKey, setEditorKey] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [periodType, setPeriodType] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    content: true,
  });

  // 템플릿 로딩 (수정 모드일 때만)
  useEffect(() => {
    const load = async () => {
      if (isNew) {
        setTitle("");
        setContent("");
        setPeriodType("weekly");
        setLoading(false);
        return;
      }

      try {
        const data = await getUserTemplate(templateId);
        setTitle(data.title || "");
        setContent(data.template || "");
        setPeriodType(data.period_type || "weekly");
        setEditorKey((k) => k + 1);
      } catch (err) {
        console.error("템플릿 불러오기 실패:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [templateId, isNew]);

  // 에디터 내용 동기화 (로드 후)
  useEffect(() => {
    if (editorRef.current && typeof content === "string") {
      editorRef.current.setContent(content || "");
    }
  }, [content]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 뒤로가기
  const handleBack = () => {
    if (window.confirm("작성 중인 내용이 저장되지 않습니다. 나가시겠습니까?")) {
      navigate(`/project/${id}/const/userTemplate`);
    }
  };

  // 저장
  const handleSave = async () => {
    if (!title.trim()) {
      alert("템플릿 제목을 입력해주세요.");
      return;
    }

    const templateHtml = editorRef.current?.getContent?.() ?? content ?? "";

    const payload = {
      title,
      template: templateHtml,
      project: id,
      period_type: periodType,
    };

    setSaving(true);
    try {
      if (isNew) {
        const created = await saveUserTemplate(payload);
        alert("템플릿이 생성되었습니다!");

        if (created?.id) {
          navigate(`/project/${id}/const/userTemplate/view/${created.id}`, {
            replace: true,
          });
        } else {
          navigate(`/project/${id}/const/userTemplate`);
        }
      } else {
        await updateUserTemplate(templateId, payload);
        alert("템플릿이 수정되었습니다!");

        const updated = await getUserTemplate(templateId);
        setTitle(updated.title || "");
        setContent(updated.template || "");
        setPeriodType(updated.period_type || "weekly");
      }
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (isNew) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await deleteUserTemplate(templateId);
      alert("템플릿이 삭제되었습니다!");
      navigate(`/project/${id}/const/userTemplate`);
    } catch (err) {
      console.error("삭제 실패:", err);
      alert("삭제에 실패했습니다.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리 · 사용자 템플릿</p>
          <h1 className="text-xl font-bold text-slate-900">
            {isNew ? "새 템플릿 작성" : "템플릿 수정"}
          </h1>
        </header>

        <div className="space-y-3">
          {/* 기본 정보 섹션 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.basic}
            onToggle={() => toggleSection("basic")}
            icon={<FileText size={16} className="text-blue-500" />}
          >
            <div className="space-y-3">
              {/* 템플릿 제목 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  템플릿 제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="템플릿 제목을 입력하세요"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-blue-400 focus:outline-none"
                />
              </div>

              {/* 보고 구분 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  보고 구분
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPeriodType("weekly")}
                    className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${periodType === "weekly"
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    주간
                  </button>
                  <button
                    type="button"
                    onClick={() => setPeriodType("monthly")}
                    className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${periodType === "monthly"
                        ? "bg-purple-500 text-white border-purple-500"
                        : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    월간
                  </button>
                </div>
              </div>
            </div>
          </Section>

          {/* 템플릿 내용 섹션 */}
          <Section
            title="템플릿 내용"
            expanded={expandedSections.content}
            onToggle={() => toggleSection("content")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
              <TinyMCEeditor
                key={editorKey}
                height={400}
                initialValue={content}
                editorRef={editorRef}
              />
            </div>
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className={`grid gap-3 ${isNew ? "grid-cols-2" : "grid-cols-3"}`}>
          <button
            type="button"
            onClick={handleBack}
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
          >
            <ChevronLeft size={18} />
            취소
          </button>

          {!isNew && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-full h-11 rounded-full border border-red-300 bg-white text-red-500 text-sm font-medium hover:bg-red-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
            >
              <Trash2 size={18} />
              삭제
            </button>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save size={18} />
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}
